
import React from 'react';
import { useChatHistory, ChatHistoryItem } from '@/hooks/useChatHistory';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { FileCheck, Trash2, MessageSquare } from 'lucide-react';

const ChatHistoryCard: React.FC<{ 
  chat: ChatHistoryItem;
  onDeleteClick: (chatId: string) => void;
}> = ({ chat, onDeleteClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/builder`, { state: { chatId: chat.id } });
    window.dispatchEvent(new CustomEvent('open-chat', { 
      detail: { chatId: chat.id } 
    }));
  };

  return (
    <Card 
      className="mb-3 bg-black border-gray-800 transition-colors cursor-pointer hover:bg-gray-900/30"
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-jade" />
              <h3 className="text-base font-medium text-white truncate">{chat.title}</h3>
              {chat.has_signed_document && (
                <Badge variant="outline" className="ml-2 text-xs bg-green-900/20 text-green-400 border-green-600/40">
                  <FileCheck className="h-3 w-3 mr-1" /> Dokument signiert
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {formatDistanceToNow(new Date(chat.updated_at), { addSuffix: true, locale: de })}
            </p>
          </div>
          
          <div className="flex items-start">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0 opacity-70 hover:opacity-100" 
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                onDeleteClick(chat.id);
              }}
            >
              <Trash2 className="h-4 w-4 text-red-400" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Chats: React.FC = () => {
  const {
    chatHistory,
    isLoadingChats,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteClick,
    handleDeleteConfirm
  } = useChatHistory();

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Chat Verlauf</h1>
        <Button 
          variant="default" 
          size="sm" 
          className="bg-jade hover:bg-jade/90"
          onClick={() => window.dispatchEvent(new CustomEvent('start-new-conversation'))}
        >
          Neue Konversation
        </Button>
      </div>

      {isLoadingChats ? (
        // Loading skeletons
        Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="mb-3 bg-black border-gray-800">
            <CardContent className="p-4">
              <div className="flex justify-between">
                <div className="flex-1">
                  <Skeleton className="h-5 w-3/4 bg-gray-900" />
                  <Skeleton className="h-3 w-1/3 mt-2 bg-gray-900" />
                </div>
                <Skeleton className="h-7 w-7 rounded-full bg-gray-900" />
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        chatHistory.length > 0 ? (
          chatHistory.map(chat => (
            <ChatHistoryCard 
              key={chat.id} 
              chat={chat} 
              onDeleteClick={handleDeleteClick} 
            />
          ))
        ) : (
          <div className="text-center py-12 bg-black border border-gray-800">
            <FileCheck className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <h3 className="text-xl font-medium text-gray-200">Keine Konversationen</h3>
            <p className="text-gray-400 mt-2 mb-4">Sie haben noch keine Chat-Konversationen gestartet</p>
            <Button 
              onClick={() => window.dispatchEvent(new CustomEvent('start-new-conversation'))}
              className="bg-jade hover:bg-jade/90"
            >
              Neue Konversation starten
            </Button>
          </div>
        )
      )}
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-black border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Konversation löschen</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Möchten Sie diese Konversation wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-900 hover:bg-gray-800 text-white border-0">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Chats;
